import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/i18n/use-translation";

type Tier = 1 | 2 | 3 | 4;

// POS2-788 — tiers are no longer colour-coded on the transactions feed (the
// red/amber/blue/emerald strip was removed to cut visual noise). The tier reads
// from the "L{n}" label; the badge is a single neutral grey for every tier, with
// the full meaning on aria-label for screen readers.
/** Literal keys in a lookup table, resolved at render — the meaning is spoken to a screen
 * reader through `waste.tierAria`, so an English constant here put an English word inside a
 * German sentence (POS2-1134). */
const TIER_MEANING_KEYS: Record<Tier, string> = {
  4: "reference.tier.l4",
  3: "reference.tier.l3",
  2: "reference.tier.l2",
  1: "reference.tier.l1",
};

function toTier(level: number | null | undefined): Tier | null {
  return level === 1 || level === 2 || level === 3 || level === 4 ? level : null;
}

type Props = {
  level: number | null | undefined;
  className?: string;
};

export function TierBadge({ level, className }: Props) {
  const { t } = useTranslation();
  const tier = toTier(level);
  if (tier == null) {
    return (
      <Badge
        className={cn("border-transparent bg-gray-50 text-gray-400", className)}
        data-testid="tier-badge"
        data-tier={level == null ? "none" : "unknown"}
      >
        {level == null ? t("waste.unanalysed") : t("waste.tierUnknown")}
      </Badge>
    );
  }

  return (
    <Badge
      className={cn("border-transparent bg-gray-100 text-gray-600", className)}
      data-testid="tier-badge"
      data-tier={`L${tier}`}
      aria-label={t("waste.tierAria", { tier, meaning: t(TIER_MEANING_KEYS[tier]) })}
    >
      L{tier}
    </Badge>
  );
}
